import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ShortcutsSelect from './ShortcutsSelect';
import { type DateRangeType } from './types';
import type { PickersActionBarProps } from '@mui/x-date-pickers';
import React from 'react';

interface CustomActionbarProps extends PickersActionBarProps {
  shortcutItems?: DateRangeType[];
  selectedDefaultId?: string;
  handleAccept: () => void;
  handleClose: () => void;
  handleDefaultSelect: (id: string) => void;
  isValidRange: boolean;
  showReset?: boolean;
  handleReset?: () => void;
}

type ResetActionButtonProps = {
  showReset?: boolean;
  onReset?: () => void;
};

function ResetActionButton({ showReset, onReset }: ResetActionButtonProps) {
  if (!showReset || !onReset) return null;
  return (
    <Button variant='text' id='reset-date-range' onClick={onReset} data-testid='reset-date-range-button'>
      Reset date
    </Button>
  );
}

type DefaultSelectorProps = {
  shortcutItems?: DateRangeType[];
  selectedDefaultId?: string;
  handleDefaultSelect: (id: string) => void;
};

function DefaultSelector({ shortcutItems, selectedDefaultId, handleDefaultSelect }: DefaultSelectorProps) {
  if (!shortcutItems || !selectedDefaultId) return null;
  return (
    <Stack direction='row' gap={1} alignItems='center'>
      <Typography variant='captionRegular' color='text.secondary'>
        Default to show
      </Typography>
      <ShortcutsSelect
        shortcutItems={shortcutItems}
        selectedId={selectedDefaultId}
        handleSelect={handleDefaultSelect}
      />
    </Stack>
  );
}

function CustomActionbar({
  shortcutItems,
  selectedDefaultId,
  handleAccept,
  handleClose,
  handleDefaultSelect,
  isValidRange,
  showReset,
  handleReset,
}: CustomActionbarProps) {
  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'border.light',
        padding: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gridColumn: '1/3',
        gridRow: '3',
      }}
    >
      <Stack direction='row' gap={1} alignItems='center'>
        <ResetActionButton showReset={showReset} onReset={handleReset} />
        <DefaultSelector
          shortcutItems={shortcutItems}
          selectedDefaultId={selectedDefaultId}
          handleDefaultSelect={handleDefaultSelect}
        />
      </Stack>
      <Stack direction='row' gap={1} justifyContent='flex-end'>
        <Button variant='outlined' id='cancel-date-range' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='contained' id='apply-date-range' onClick={handleAccept} disabled={!isValidRange}>
          Apply Dates
        </Button>
      </Stack>
    </Box>
  );
}

export default CustomActionbar;
